/* Header: Sticky-Verhalten, mobiler Drawer mit Ebenen, Mega-Menü, mobile Suche, Warenkorb-Zähler. */

const DESKTOP = window.matchMedia('(min-width: 1024px)');
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class KgHeader extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('.header__nav');
    this.menuOpenButton = this.querySelector('[data-menu-open]');
    this.backButton = this.querySelector('[data-menu-back]');
    this.items = [...this.querySelectorAll('.menu__item--parent')].map((item) => {
      const toggle = item.querySelector(':scope > .menu__row > .menu__toggle');
      const link = item.querySelector(':scope > .menu__row > .menu__link');
      const panel = toggle ? document.getElementById(toggle.getAttribute('aria-controls')) : null;
      return { item, toggle, link, panel, timer: null };
    });
    this.onDrawerKeydown = this.onDrawerKeydown.bind(this);
    this.onDocumentClick = (event) => {
      if (DESKTOP.matches && !this.contains(event.target)) this.closeItems();
    };
    this.onBreakpointChange = () => {
      this.closeDrawer();
      this.closeItems();
    };

    this.setupHeight();
    this.setupSticky();
    this.setupDrawer();
    this.setupMegaMenu();
    this.setupSearchOverlay();
    this.setupSearchWidth();
    document.addEventListener('click', this.onDocumentClick);
    DESKTOP.addEventListener('change', this.onBreakpointChange);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.widthObserver?.disconnect();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDrawerKeydown);
    document.documentElement.classList.remove('is-scroll-locked');
    DESKTOP.removeEventListener('change', this.onBreakpointChange);
  }

  get openEntry() {
    return this.items.find((entry) => entry.item.classList.contains('is-open'));
  }

  setupHeight() {
    const update = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    this.resizeObserver = new ResizeObserver(update);
    this.resizeObserver.observe(this);
    update();
  }

  setupSticky() {
    if (this.dataset.sticky === 'none') return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        /*
          Liegt die Seite fest, weil ein Schuber offen ist, bleibt der Header wie er ist. Sonst
          faehrt er ein, sobald in einem Feld die Tastatur aufgeht — der Browser meldet dabei
          Bewegung, die es auf der Seite gar nicht gibt. Hinter einem offenen Schuber ist jede
          Bewegung des Headers ohnehin nur Unruhe.
        */
        if (document.documentElement.hasAttribute('data-gesperrt')) {
          lastY = window.scrollY;
          ticking = false;
          return;
        }
        const y = window.scrollY;
        const delta = y - lastY;
        this.classList.toggle('is-scrolled', y > 8);
        const menuOpen = this.classList.contains('is-menu-open') || Boolean(this.openEntry);
        if (this.dataset.sticky === 'scroll_up' && !menuOpen) {
          if (delta > 6 && y > this.offsetHeight) this.classList.add('is-hidden');
          else if (delta < -6 || y <= 0) this.classList.remove('is-hidden');
        }
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  setupDrawer() {
    if (!this.nav || !this.menuOpenButton) return;
    this.menuOpenButton.addEventListener('click', () => this.openDrawer());
    this.querySelectorAll('[data-menu-close]').forEach((element) => {
      element.addEventListener('click', () => {
        this.closeDrawer();
        /* Dieselbe Flaeche liegt am Rechner unter dem Mega-Menue: Ein Klick darauf schliesst es. */
        if (DESKTOP.matches) this.closeItems();
      });
    });
    /* Weit genug nach unten gezogen: dasselbe wie ein Tipp auf das Kreuz. */
    this.nav.addEventListener('sheet:dismiss', () => this.closeDrawer());
    this.backButton?.addEventListener('click', () => {
      const entry = this.openEntry;
      if (!entry) return;
      this.setEntry(entry, false);
      entry.toggle?.focus();
    });
  }

  /* Schuber-Darstellung ist eine Einstellung und gilt nur auf kleinen Bildschirmen. */
  get istSchuber() {
    return this.hasAttribute('data-menu-sheet') && !DESKTOP.matches;
  }

  openDrawer() {
    /*
      In der Schuber-Darstellung geht die Navigation als echter Dialog auf. Damit erbt sie
      alles, was fuer die Schuber gebaut ist: die klebende Abdunklung, die Farbe der
      Browserleisten und das Abraeumen beim Schliessen.

      Die Abdunklung muss zwei Bildaufbauten vorher stehen — Safari misst die Farbe seiner
      Leisten in dem Moment, in dem sich die oberste Ebene aendert, und liest dabei das
      zuletzt gezeichnete Bild. Ausfuehrlich in assets/dialog.js.
    */
    if (this.istSchuber && !this.nav.open) {
      if (!this.dunkelBereit) {
        document.documentElement.setAttribute('data-schuber-dunkel', '');
        this.dunkelBereit = true;
        requestAnimationFrame(() => requestAnimationFrame(() => this.openDrawer()));
        return;
      }
      this.dunkelBereit = false;
      /* Derselbe Zaehler wie in dialog.js, damit die Reihenfolge stimmt. */
      const wurzel = document.documentElement;
      wurzel.dataset.schuberRang = String(Number(wurzel.dataset.schuberRang || 0) + 1);
      this.nav.dataset.schuberRang = wurzel.dataset.schuberRang;
      this.nav.showModal();
      document.dispatchEvent(new CustomEvent('schuber:stapel'));
    }

    this.classList.add('is-menu-open');
    this.classList.remove('is-hidden');
    this.menuOpenButton.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('is-scroll-locked');
    document.addEventListener('keydown', this.onDrawerKeydown);
    this.nav.setAttribute('tabindex', '-1');
    this.nav.focus({ preventScroll: true });
    /*
      Anmelden wie ein Schuber: Darauf hin weicht die Ankuendigungszeile, wenn die Seite ganz
      oben steht. Ueber ein Ereignis statt ueber einen Import — relative Modul-Importe tragen
      im Theme keine Version in der Adresse und bleiben im Zwischenspeicher haengen.
    */
    if (this.istSchuber) {
      document.dispatchEvent(new CustomEvent('sheet:open'));
      /* Ziehen freigeben — die Mechanik dafuer steht in dialog.js und gilt fuer jede Flaeche. */
      this.nav.setAttribute('data-sheet-drag-aktiv', '');
    }
  }

  closeDrawer() {
    if (!this.classList.contains('is-menu-open')) return;
    if (this.istSchuber) document.dispatchEvent(new CustomEvent('sheet:close'));

    /* Erst ausfahren lassen, dann schliessen — sonst waere der Dialog sofort weg. */
    if (this.nav.open) {
      this.nav.classList.add('is-closing');
      const fertig = () => {
        /* Erst die Abdunklung, dann die Klasse — Begruendung in dialog.js, finish(). */
        if (
          !document.querySelector(
            'dialog.drawer[open]:not(.is-closing), dialog.header__nav[open]:not(.is-closing)'
          )
        ) {
          document.documentElement.removeAttribute('data-schuber-dunkel');
        }
        this.nav.classList.remove('is-closing');
        this.nav.close();
        document.dispatchEvent(new CustomEvent('schuber:stapel'));
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) fertig();
      else window.setTimeout(fertig, 250);
    }

    this.nav.removeAttribute('data-sheet-drag-aktiv');
    this.classList.remove('is-menu-open');
    this.menuOpenButton.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('is-scroll-locked');
    document.removeEventListener('keydown', this.onDrawerKeydown);
    this.closeItems();
    this.anfangZeigen();
    this.menuOpenButton.focus();
  }

  /*
    Beim Schliessen zurueck an den Anfang scrollen, damit das Menue beim naechsten
    Oeffnen wieder oben steht statt an der zuletzt besuchten Stelle.

    Bewusst ueber alle gescrollten Elemente statt ueber ein bestimmtes: Welcher Kasten
    scrollt, haengt an der Darstellung — Schuber oder Zeile, Hauptebene oder Untermenue.
  */
  anfangZeigen() {
    if (this.nav.scrollTop) this.nav.scrollTop = 0;
    this.nav.querySelectorAll('*').forEach((teil) => {
      if (teil.scrollTop) teil.scrollTop = 0;
    });
  }

  onDrawerKeydown(event) {
    if (event.key === 'Escape') {
      const entry = this.openEntry;
      if (entry) {
        this.setEntry(entry, false);
        entry.toggle?.focus();
      } else {
        this.closeDrawer();
      }
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.nav.querySelectorAll(FOCUSABLE)].filter((element) =>
      element.checkVisibility ? element.checkVisibility({ visibilityProperty: true }) : element.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setupMegaMenu() {
    this.items.forEach((entry) => {
      const { item, toggle, link, panel } = entry;
      /*
        Haengt an einem Hauptpunkt nur ein mobiler Teaser, gibt es sein Untermenue nur auf dem
        Handy. Am Rechner bliebe es leer, also oeffnet es dort gar nicht erst.
      */
      const nurHandy = item.classList.contains('menu__item--panel-mobile');
      const nurPc = item.classList.contains('menu__item--panel-desktop');
      const stumm = () => (DESKTOP.matches ? nurHandy : nurPc);
      toggle?.addEventListener('click', () => {
        if (stumm()) return;
        this.toggleEntry(entry, !item.classList.contains('is-open'));
      });
      link?.addEventListener('click', (event) => {
        /* Auf dem Handy oeffnet der Titel das Untermenue — ausser es gaebe dort keines. */
        if (DESKTOP.matches || nurPc) return;
        event.preventDefault();
        this.toggleEntry(entry, true);
      });
      [item, panel].forEach((element) => {
        element?.addEventListener('pointerenter', (event) => {
          if (!DESKTOP.matches || event.pointerType === 'touch' || nurHandy) return;
          clearTimeout(entry.timer);
          entry.timer = setTimeout(() => this.toggleEntry(entry, true), 60);
        });
        element?.addEventListener('pointerleave', (event) => {
          if (!DESKTOP.matches || event.pointerType === 'touch') return;
          clearTimeout(entry.timer);
          entry.timer = setTimeout(() => this.toggleEntry(entry, false), 150);
        });
      });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !DESKTOP.matches) return;
      const entry = this.openEntry;
      if (!entry) return;
      this.toggleEntry(entry, false);
      entry.toggle?.focus();
    });

    this.nav?.addEventListener('focusout', (event) => {
      if (DESKTOP.matches && event.relatedTarget && !this.nav.contains(event.relatedTarget)) this.closeItems();
    });
  }

  toggleEntry(entry, open) {
    if (open) {
      this.items.forEach((other) => {
        if (other !== entry) this.setEntry(other, false);
      });
    }
    this.setEntry(entry, open);
    if (open && !DESKTOP.matches) {
      entry.panel?.scrollTo({ top: 0 });
      this.backButton?.focus();
    }
  }

  setEntry(entry, open) {
    entry.item.classList.toggle('is-open', open);
    entry.panel?.classList.toggle('is-open', open);
    entry.toggle?.setAttribute('aria-expanded', String(open));
    this.classList.toggle('is-sub-open', Boolean(this.openEntry));
    /*
      Oberkante der Abdunklung (Einstellung „Flaeche unter dem Mega-Menue abdunkeln"): die
      Unterkante der Kopfzeile, gemessen am Bildschirm. Ueber dem Header kann die Promoleiste
      stehen, deshalb nicht --header-height nehmen — die Kante laege zu hoch und die Kopfzeile
      waere selbst halb verdunkelt.
    */
    if (this.openEntry) {
      this.style.setProperty('--mega-scrim-top', `${Math.round(this.getBoundingClientRect().bottom)}px`);
    }
  }

  closeItems() {
    this.items.forEach((entry) => this.setEntry(entry, false));
  }

  setupSearchWidth() {
    const bar = this.querySelector('.header__bar');
    const logo = this.querySelector('.header__logo-wrapper');
    const actions = this.querySelector('.header__actions');
    const search = this.querySelector('.header__search--desktop');
    if (!bar || !logo || !actions || !search) return;
    const update = () => {
      if (!DESKTOP.matches || this.classList.contains('header--menu-inline')) return;
      const barStyle = getComputedStyle(bar);
      const inner = bar.clientWidth - parseFloat(barStyle.paddingLeft) - parseFloat(barStyle.paddingRight);
      const gap = parseFloat(barStyle.columnGap) || 0;
      const overhang = Math.max(0, -parseFloat(getComputedStyle(actions).marginInlineEnd) || 0);
      const free = inner + overhang - logo.getBoundingClientRect().width - gap * 2 - actions.getBoundingClientRect().width;
      const available = search.getBoundingClientRect().width + free - 2;
      bar.style.setProperty('--header-available', `${Math.max(160, Math.floor(available))}px`);
    };
    this.widthObserver = new ResizeObserver(update);
    [bar, logo, actions, search].forEach((element) => this.widthObserver.observe(element));
    document.fonts?.ready.then(update);
    update();
  }

  setupSearchOverlay() {
    const overlay = this.querySelector('[data-search-overlay]');
    if (!overlay) return;
    overlay.addEventListener('click', () => {
      this.querySelector('.header__search--desktop kg-predictive-search')?.close();
      document.activeElement?.blur();
    });
  }

}

customElements.define('kg-header', KgHeader);

class KgCartCount extends HTMLElement {
  connectedCallback() {
    this.onUpdate = (event) => {
      const count = event.detail?.cart?.item_count ?? event.detail?.item_count;
      this.update(count);
    };
    document.addEventListener('cart:updated', this.onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onUpdate);
  }

  update(count) {
    if (typeof count !== 'number') return;
    this.textContent = count;
    this.dataset.count = count;
    this.hidden = count === 0;
    this.stups(count);
  }

  /*
    Kurzer Stupser, wenn sich die Zahl aendert. Nicht beim ersten Zeichnen und nicht, wenn die
    Zahl gleich bleibt — sonst huepfte die Blase bei jedem Seitenaufruf. Anders als bei den
    Neuigkeiten wird nicht aus dem Nichts eingeblendet, die Blase ist ja schon da.

    Dasselbe steht im jeweils anderen Zaehler (assets/header.js und assets/wishlist.js). Ein
    gemeinsamer Import schied aus: Relative Modulpfade im Theme tragen keine Version, Browser
    behalten dann die alte Datei im Zwischenspeicher.
  */
  stups(count) {
    const vorher = this.letzterStand;
    this.letzterStand = count;
    if (vorher === undefined || vorher === count || count === 0) return;
    this.classList.remove('is-bump');
    /* Lesen erzwingt den Umbruch, sonst startet dieselbe Animation nicht neu */
    void this.offsetWidth;
    this.classList.add('is-bump');
    this.addEventListener('animationend', () => this.classList.remove('is-bump'), { once: true });
  }
}

customElements.define('kg-cart-count', KgCartCount);
